import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FolderLock, X, Plus, Link2, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("token") || "";
}

function authHeaders(json = true) {
  const h = { Authorization: `Bearer ${getToken()}` };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

const ISSUE_TYPES = [
  "discipline_investigation",
  "weingarten_violation",
  "overtime_dispute",
  "schedule_dispute",
  "safety_concern",
  "seniority_dispute",
  "pay_grievance",
  "harassment",
  "other",
];

const SOURCE_LABEL = {
  fact_finder: "Fact Finder",
  statement: "Statement",
  grievance: "Grievance Builder",
};

/**
 * Modal that previews a pre-filled case from an upstream workflow (Fact Finder
 * or Grievance Builder), lets the user tweak before saving, and on confirm:
 *   1) creates a new case (or re-uses one selected from existing cases),
 *   2) links the source output to it,
 *   3) appends a timeline entry documenting the deep-link origin,
 *   4) navigates to `/case-locker/{case_id}`.
 *
 * Props:
 *   open, onClose          : visibility control
 *   sourceType             : "fact_finder" | "grievance" | "statement"
 *   sourceId               : id to attach via /cases/{id}/link
 *   prefill                : { title?, summary?, issue_type?, incident_date?,
 *                              people_involved?[], witnesses?[],
 *                              timeline_description?, status_update? }
 *   onSaved(newCase)       : optional callback after success
 */
export default function SaveToCaseLockerModal({
  open, onClose, sourceType, sourceId, prefill = {}, onSaved,
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [existing, setExisting] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState("");
  const [form, setForm] = useState({
    title: "", summary: "", issue_type: "other", incident_date: "",
    people_involved: [], witnesses: [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Prefill when opened
  useEffect(() => {
    if (!open) return;
    setErr("");
    setMode("new");
    setSelectedExisting("");
    setForm({
      title: prefill.title || defaultTitle(sourceType),
      summary: prefill.summary || "",
      issue_type: prefill.issue_type || "other",
      incident_date: prefill.incident_date || new Date().toISOString().slice(0, 10),
      people_involved: prefill.people_involved || [],
      witnesses: prefill.witnesses || [],
    });
    // Load existing cases for the "link to existing" option
    (async () => {
      try {
        const res = await fetch(`${API}/api/case-locker/cases?limit=20`, {
          headers: authHeaders(false),
        });
        const data = await res.json();
        // Defensive slice: cap at 20 regardless of what the API returns.
        setExisting((data.cases || []).slice(0, 20));
      } catch { setExisting([]); }
    })();
  }, [open, sourceType, prefill]);

  async function save() {
    setSaving(true);
    setErr("");
    try {
      let caseId;
      let created;
      if (mode === "new") {
        if (!form.title.trim()) throw new Error("Title is required");
        const res = await fetch(`${API}/api/case-locker/cases`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.detail || "Failed to create case");
        caseId = data.case.case_id;
        created = data.case;
      } else {
        if (!selectedExisting) throw new Error("Select a case to link to");
        caseId = selectedExisting;
      }

      // Link the source output to the case
      if (sourceId) {
        const linkRes = await fetch(`${API}/api/case-locker/cases/${caseId}/link-output`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({ output_type: sourceType, output_id: sourceId }),
        });
        if (!linkRes.ok) {
          // Surface the error instead of silently swallowing
          const t = await linkRes.text().catch(() => "");
          throw new Error(`Failed to link output: ${t.slice(0, 120) || linkRes.status}`);
        }
      }

      // Append a contextual timeline entry on top of the link-output's own entry
      const desc =
        prefill.timeline_description ||
        `Linked from ${SOURCE_LABEL[sourceType] || sourceType}`;
      await fetch(`${API}/api/case-locker/cases/${caseId}/timeline`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          action_type: `linked_${sourceType}_context`,
          description: desc,
        }),
      });

      // Optional status update (e.g. from grievance builder: "grievance_filed")
      if (prefill.status_update) {
        await fetch(`${API}/api/case-locker/cases/${caseId}/status`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({
            status: prefill.status_update,
            reason: `Auto-update after linking ${SOURCE_LABEL[sourceType] || sourceType}`,
          }),
        });
      }

      onClose && onClose();
      onSaved && onSaved({ case_id: caseId, case: created });
      navigate(`/case-locker/${caseId}`);
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    }
    setSaving(false);
  }

  if (!open) return null;

  return (
    <div className="stcl-overlay" data-testid="save-to-case-locker-modal" onClick={onClose}>
      <div className="stcl-modal" onClick={(e) => e.stopPropagation()}>
        <style>{stclStyles}</style>
        <div className="stcl-header">
          <div className="stcl-title">
            <FolderLock size={16} /> Save to Case Locker
          </div>
          <button type="button" className="stcl-close" onClick={onClose} data-testid="stcl-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="stcl-tabs">
          <button
            className={`stcl-tab ${mode === "new" ? "active" : ""}`}
            onClick={() => setMode("new")}
            data-testid="stcl-tab-new"
          >
            <Plus size={13} /> New case
          </button>
          <button
            className={`stcl-tab ${mode === "existing" ? "active" : ""}`}
            onClick={() => setMode("existing")}
            data-testid="stcl-tab-existing"
            disabled={!existing.length}
          >
            <Link2 size={13} /> Link to existing
          </button>
        </div>

        {mode === "new" ? (
          <div className="stcl-body">
            <label className="stcl-label">Title</label>
            <input
              className="stcl-input"
              data-testid="stcl-title-input"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            />
            <div className="stcl-row">
              <div className="stcl-col">
                <label className="stcl-label">Issue type</label>
                <select
                  className="stcl-input"
                  data-testid="stcl-issue-select"
                  value={form.issue_type}
                  onChange={(e) => setForm(f => ({ ...f, issue_type: e.target.value }))}
                >
                  {ISSUE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div className="stcl-col">
                <label className="stcl-label">Incident date</label>
                <input
                  type="date"
                  className="stcl-input"
                  data-testid="stcl-date-input"
                  value={form.incident_date}
                  onChange={(e) => setForm(f => ({ ...f, incident_date: e.target.value }))}
                />
              </div>
            </div>
            <label className="stcl-label">Summary</label>
            <textarea
              className="stcl-input stcl-textarea"
              rows={3}
              data-testid="stcl-summary-input"
              value={form.summary}
              onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))}
            />
            <div className="stcl-hint">
              {(form.people_involved?.length || 0)} person/people · {(form.witnesses?.length || 0)} witness(es) will be carried over.
            </div>
          </div>
        ) : (
          <div className="stcl-body">
            <label className="stcl-label">Select an existing case to link to</label>
            <div className="stcl-existing-list" data-testid="stcl-existing-list">
              {existing.map(c => (
                <button
                  key={c.case_id}
                  type="button"
                  className={`stcl-existing-row ${selectedExisting === c.case_id ? "sel" : ""}`}
                  onClick={() => setSelectedExisting(c.case_id)}
                  data-testid={`stcl-existing-${c.case_id}`}
                >
                  <div className="stcl-existing-id">{c.case_id}</div>
                  <div className="stcl-existing-title">{c.title}</div>
                  {selectedExisting === c.case_id && <CheckCircle2 size={14} />}
                </button>
              ))}
              {!existing.length && <div className="stcl-empty">You have no cases yet. Create a new one.</div>}
            </div>
          </div>
        )}

        {err && <div className="stcl-error" data-testid="stcl-error">{err}</div>}

        <div className="stcl-footer">
          <button
            type="button"
            className="stcl-btn-ghost"
            onClick={onClose}
            data-testid="stcl-cancel-btn"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="stcl-btn-primary"
            onClick={save}
            data-testid="stcl-save-btn"
            disabled={saving || (mode === "existing" && !selectedExisting)}
          >
            {saving ? <Loader2 size={14} className="stcl-spin" /> : null}
            {mode === "new" ? "Create & Link" : "Link to Case"}
          </button>
        </div>
      </div>
    </div>
  );
}

function defaultTitle(sourceType) {
  const stamp = new Date().toISOString().slice(0, 10);
  if (sourceType === "fact_finder") return `Fact Finder Case – ${stamp}`;
  if (sourceType === "grievance") return `Grievance Case – ${stamp}`;
  if (sourceType === "statement") return `Statement Case – ${stamp}`;
  return `Case – ${stamp}`;
}

const stclStyles = `
.stcl-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  backdrop-filter: blur(6px);
  font-family: 'Inter', -apple-system, sans-serif;
}
.stcl-modal {
  width: 100%; max-width: 520px;
  background: #0f0f12;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.6);
  overflow: hidden;
  display: flex; flex-direction: column;
  max-height: 90vh;
}
.stcl-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.stcl-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 700; color: #fafafa;
}
.stcl-title svg { color: #a78bfa; }
.stcl-close {
  background: none; border: none; color: #737373;
  cursor: pointer; padding: 4px; border-radius: 6px;
  display: flex;
}
.stcl-close:hover { background: rgba(255,255,255,0.05); color: #fafafa; }

.stcl-tabs {
  display: flex; gap: 4px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.stcl-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  color: #a3a3a3; font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit;
}
.stcl-tab:hover:not(:disabled) { color: #fafafa; background: rgba(255,255,255,0.04); }
.stcl-tab.active { background: rgba(139,92,246,0.12); border-color: rgba(139,92,246,0.3); color: #c4b5fd; }
.stcl-tab:disabled { opacity: 0.4; cursor: not-allowed; }

.stcl-body { padding: 16px 18px; overflow-y: auto; flex: 1; }
.stcl-label {
  display: block; font-size: 10px; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: #737373;
  margin-bottom: 5px; margin-top: 10px;
}
.stcl-label:first-child { margin-top: 0; }
.stcl-input {
  width: 100%; padding: 9px 12px;
  border-radius: 8px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  color: #e5e5e5;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
}
.stcl-input:focus { border-color: rgba(139,92,246,0.5); }
.stcl-textarea { resize: vertical; min-height: 70px; font-family: inherit; }
.stcl-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.stcl-col { min-width: 0; }
.stcl-hint { font-size: 11px; color: #737373; margin-top: 10px; }

.stcl-existing-list { display: flex; flex-direction: column; gap: 5px; max-height: 280px; overflow-y: auto; }
.stcl-existing-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 8px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.04);
  cursor: pointer; text-align: left;
  font-family: inherit;
  color: #e5e5e5;
  transition: background 0.12s, border-color 0.12s;
}
.stcl-existing-row:hover { background: rgba(255,255,255,0.05); }
.stcl-existing-row.sel { background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.4); }
.stcl-existing-id { font-size: 10px; font-family: 'SF Mono', monospace; color: #a1a1aa; min-width: 95px; }
.stcl-existing-title { flex: 1; font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stcl-empty { padding: 24px; text-align: center; color: #737373; font-size: 12px; font-style: italic; }

.stcl-error {
  margin: 0 18px; padding: 8px 12px;
  border-radius: 8px;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.25);
  color: #fca5a5; font-size: 12px; font-weight: 600;
}

.stcl-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(0,0,0,0.25);
}
.stcl-btn-ghost, .stcl-btn-primary {
  padding: 8px 16px; border-radius: 8px;
  font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  display: inline-flex; align-items: center; gap: 6px;
}
.stcl-btn-ghost {
  background: transparent; color: #a3a3a3;
  border: 1px solid rgba(255,255,255,0.08);
}
.stcl-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,0.05); color: #fafafa; }
.stcl-btn-primary {
  background: linear-gradient(135deg, rgba(139,92,246,0.9), rgba(167,139,250,0.85));
  color: #fafafa; border: none;
}
.stcl-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.stcl-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.stcl-spin { animation: stcl-spin 0.9s linear infinite; }
@keyframes stcl-spin { to { transform: rotate(360deg); } }
`;
